import React from "react"
import { useTranslation } from "react-i18next"

import * as BikeApi from "features/bike/api/BikeApi"
import * as UserApi from "features/api/UserApi"

import { useAlert, useAuth, useBikeStore } from "hooks"

import { isDefined } from "utils/validation"
import {
	bikeAccountTypes,
	MFC_OPERATOR,
	BC_OPERATOR,
	MFC_RETAIL_GENDER,
	REQUEST_ONGOING,
	BACK_PROPERTY_TRANSFER_REQUEST,
	BUY_PROPERTY_TRANSFER_REQUEST,
	OCODE_GENDERS_LIST,
	operators,
} from "utils/constants"
import { getProtectedEmail } from "utils/functions"

import FormElement from "components/Form/FormElement"
import { CheckIcon } from "components/Elements/Icons"
import { ErrorResponse } from "models"

const defaultFormFields = {
	ownerMail: "",
	ownerNewMail: "",
	ownerConfirmNewMail: "",
}

const defaultFormEvents = {
	ready: true,
}

export const FormBikeSold = ({ isAdmin, disabled }) => {
	const { currentBike: bike, isSeller, fetchBike } = useBikeStore()

	const [formFields, setFormFields] = React.useState(defaultFormFields)
	const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)

	const { entity, user, logout } = useAuth()
	const isBO = entity?.entity_data?.entity_permission?.role === "MANAGEENTITIES"
	const isAdminOCode = OCODE_GENDERS_LIST.includes(user?.gender)

	React.useEffect(() => {
		setFormFields((s) => ({
			...s,
			ownerMail: bike?.owner?.mail ?? "",
		}))

		if (bike?.owner?.mail && disabled && !isAdminOCode) {
			setFormFields((s) => ({
				...s,
				ownerMail: getProtectedEmail(bike?.owner?.mail),
			}))
		}
	}, [bike]) // eslint-disable-line

	const { alert } = useAlert()
	const { t } = useTranslation()

	// Chaque vélo porte la notion d'"account_type", à savoir est-ce que le propriétaire du vélo à un compte ou non et si oui, sur quelle plateforme (Wallet, No wallet ou Garage à vélos)
	// Chaque compte est préfixé par le nom de l'opérateur (ex: BC_CONSO_ACCOUNT_VERIFIED)
	// Le tableau bikeAccountTypes liste tous les variantes de type de compte possibles
	// Pour savoir si le propriétaire a ajouté le vélo dans ce compte, on se base sur "mail_verified". Tant que ce n'est pas fait le boolean restera à false
	const isOwnerMailVerified = bike?.mailVerified
	const accountType = isOwnerMailVerified
		? bikeAccountTypes.find(({ suffix }) => bike?.accountType === `${bike?.operator}${suffix}`)
		: bikeAccountTypes.find(
				({ suffix }) =>
					`${bike?.operator}_CONSO_ACCOUNT_NOT_VERIFIED` === `${bike?.operator}${suffix}`
		  )

	const isWalletAccountType =
		accountType?.id === "wallet_verified" || accountType?.id === "wallet_not_migrated"

	const isNoWalletVerifiedBCAccountType =
		accountType?.id === "nowallet_verified" && bike?.operator === BC_OPERATOR
	const walletName = t("account_type.owallet", { ns: "bike" })

	const operatorBikeName = operators.find((operator) => operator.id_api === bike?.operator)?.name

	const isBikeTransferRequestOnGoing = bike?.propertyTransferRequest?.status === REQUEST_ONGOING
	const isBackToStoreTransferRequested =
		bike?.propertyTransferRequest?.context === BACK_PROPERTY_TRANSFER_REQUEST

	const handleInputChange = (event) => {
		const target = event.target
		const value = target.type === "checkbox" ? target.checked : target.value
		const name = target.name

		setFormFields({
			...formFields,
			[name]: value,
		})
	}

	const handleValidation = React.useCallback((name, errs) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
	}, [])

	const handleSubmit = async (event) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormEvents({
					...formEvents,
					ready: false,
				})

				const newBike = {
					bike_id: bike.code,
					bike_owner: {
						newmail: formFields?.ownerNewMail.trim(),
					},
					row_version: bike.row_version,
				}

				// console.log("newBike", newBike)

				const { getError } = await BikeApi.editBike(newBike)
				if (getError()) throw getError()

				alert("success", t("edit_success", { ns: "bike" }))
				// on renvoie les mails preuve d'achat et mdp
				sendMail()
			} catch (error) {
				console.error(error)
				const erroMsg = (error as ErrorResponse)?.message?.toString()
				if (erroMsg) alert("error", erroMsg)

				if (["BikeVersionMismatchException"].includes((error as ErrorResponse)?.code))
					fetchBike(bike.code)
				if (["Access token is required."].includes((error as ErrorResponse)?.message))
					logout()
			} finally {
				setFormEvents((s) => ({
					...s,
					ready: true,
				}))
			}
		}
	}

	// Manage send mail
	// -----------------------
	const [btnDisabled, setBtnDisabled] = React.useState(false)
	const [showUpdateMail, setShowUpdateMail] = React.useState(false)

	const sendMail = async () => {
		try {
			setBtnDisabled(true)
			let template = "default"
			if (bike?.operator === MFC_OPERATOR) {
				if (isBO) {
					const store_id = bike?.storeId

					const { res: entity_bike } =
						store_id.substring(0, 3) === "ST-"
							? await UserApi.getEntity(store_id.substring(3, store_id.length))
							: await UserApi.getEntityByOldStoreId(store_id)

					if (entity_bike) {
						const operator_detail = entity_bike?.data_operators.find(
							(oneOperator) => oneOperator.operator === bike?.operator
						)
						const operator_gender = operator_detail?.entity_data?.operator_gender
						if (isDefined(operator_gender) && operator_gender !== MFC_RETAIL_GENDER) {
							template = operator_detail?.entity_data?.operator_gender
						}
					}
				} else {
					const operator_detail = entity?.data_operators.find(
						(oneOperator) => oneOperator.operator === bike?.operator
					)
					const operator_gender = operator_detail?.entity_data?.operator_gender
					if (isDefined(operator_gender) && operator_gender !== MFC_RETAIL_GENDER) {
						template = operator_detail?.entity_data?.operator_gender
					}
				}
			}

			const params = bike?.accountActivated
				? "&send_notification_by_mail=disable&send_receipt_by_mail=" + template
				: "&send_notification_by_mail=" + template + "&send_receipt_by_mail=" + template

			const { getError } = await UserApi.resendMails(bike.code, params)
			if (getError()) throw getError()
			alert("success", t("mail_code_success", { ns: "form" }))
			setShowUpdateMail(false)
			fetchBike(bike.code)
		} catch (error) {
			console.error(error)
			alert("error", (error as ErrorResponse)?.message?.toString())
		} finally {
			setBtnDisabled(false)
		}
	}

	return (
		<form className="">
			<section>
				<h2 className="c-h2 font-semibold mb-5 uppercase">
					{t("owner_title", { ns: "bike" })}
				</h2>

				{/*  Specific info */}
				{accountType && (isSeller || isAdmin) && (
					<>
						{/* case if “BC_NOWALLET_VERIFIED” */}
						{isNoWalletVerifiedBCAccountType && (
							<p className="mb-5">
								{t("account_type.description_create_account", {
									ns: "bike",
									operator: operatorBikeName,
								})}
							</p>
						)}

						{!isNoWalletVerifiedBCAccountType && (
							<>
								{accountType.description && (
									<div className="flex items-center">
										<p className="u-mg-right-s">
											{t(`${accountType.description}`, {
												operator: isWalletAccountType
													? walletName
													: operatorBikeName,
											})}
										</p>
										{accountType?.iconDescription === "check" && (
											<CheckIcon size={20} color="#4caf50" />
										)}
									</div>
								)}
							</>
						)}
						{accountType?.emailDescription && (
							<p className="mb-5">{t(`${accountType.emailDescription}`)}</p>
						)}
					</>
				)}

				<div className="l-grid u-mg-negative-hz-m">
					<div className="lg:w-1/2 w-full p-5">
						{/* ownerMail */}
						<FormElement
							value={formFields.ownerMail}
							name="ownerMail"
							type="email"
							label={t("owner.mail", { ns: "bike" })}
							className="mb-5"
							readonly={bike?.accountActivated ? true : false}
							disabled={true}
						/>
					</div>

					{!disabled && (
						<div className="lg:w-1/2 w-full p-5">
							<button
								type="button"
								className="c-btn c-btn--primary px-5"
								onClick={sendMail}
								disabled={!formFields.ownerMail || btnDisabled}
							>
								{t("cta_send_back_email", { ns: "bike" })}
							</button>
						</div>
					)}
				</div>

				{!bike?.accountActivated && showUpdateMail && (
					<>
						<div className="l-grid u-mg-negative-hz-m">
							<div className="lg:w-1/2 w-full py-1 px-5">
								{/* ownerNewMail */}
								<FormElement
									value={formFields.ownerNewMail}
									name="ownerNewMail"
									type="email"
									label={t("owner.newMail", { ns: "bike" })}
									className="mb-5"
									required={showUpdateMail}
									showErrors={showLocalErrors}
									onValidate={handleValidation}
									onChange={handleInputChange}
								/>
							</div>
						</div>
						<div className="l-grid u-mg-negative-hz-m">
							<div className="lg:w-1/2 w-full py-1 px-5">
								{/* ownerConfirmNewMail */}
								<FormElement
									value={formFields.ownerConfirmNewMail}
									name="ownerConfirmNewMail"
									type="email"
									label={t("owner.newMailConfirmation", { ns: "bike" })}
									mailToConfirm={formFields.ownerNewMail}
									className="mb-5"
									required={showUpdateMail}
									showErrors={showLocalErrors}
									onValidate={handleValidation}
									onChange={handleInputChange}
								/>
							</div>
						</div>
					</>
				)}
			</section>

			<div className="l-grid">
				<div className="lg:w-1/2 w-full">
					{!bike?.accountActivated &&
						!showUpdateMail &&
						(user.permission === -1 || user.permission === 0) &&
						!disabled && (
							<>
								{isBikeTransferRequestOnGoing && (
									<p className="mb-2">
										{t("back_and_buy.cant_modify_email", {
											ns: "bike",
											propertyTransferType: isBackToStoreTransferRequested
												? BACK_PROPERTY_TRANSFER_REQUEST.toLowerCase()
												: BUY_PROPERTY_TRANSFER_REQUEST.toLowerCase(),
										})}
									</p>
								)}

								<div className="flex justify-start mb-5">
									<button
										type="button"
										className="c-btn c-btn--primary px-12"
										onClick={() => setShowUpdateMail(true)}
										disabled={isBikeTransferRequestOnGoing}
									>
										{t("global.update")}
									</button>
								</div>
							</>
						)}
					{showUpdateMail && (
						<div className="flex justify-between flex-col md:flex-row-reverse mt-5">
							{formEvents.ready && (
								<button
									type="submit"
									className="c-btn c-btn--primary px-12"
									onClick={handleSubmit}
									disabled={errors.length > 0}
								>
									{t("global.confirm")}
								</button>
							)}
							{!formEvents.ready && <div className="c-spinner"></div>}
							<button
								type="button"
								className="c-btn c-btn--primary-bordered px-12"
								onClick={() => setShowUpdateMail(false)}
							>
								{t("global.cancel")}
							</button>
						</div>
					)}
				</div>
			</div>
		</form>
	)
}
